import { API_URL, MAINTENANCE_STATUS_TIMEOUT_MS, TOOLS_API_URL } from "@config/constants";
import { parseMaintenanceStatus } from "@utils/maintenance.util";
import type { MaintenanceStatus } from "@app-types/maintenance.type";

const MAINTENANCE_PATH = "api/maintenance";

class MaintenanceService {
  private URL = TOOLS_API_URL ? `${TOOLS_API_URL}${MAINTENANCE_PATH}` : "";

  /**
   * Current maintenance status. Resolves to `null` when the service can't be read (network
   * error, non-JSON body, unset host) — callers must treat that as "no news", never as
   * "maintenance is off".
   *
   * Uses `fetch` instead of `apiClient` on purpose: this is a different origin with no Meridian
   * auth, and it must not run through the 503 interceptor that drives maintenance mode.
   */
  async getStatus(): Promise<MaintenanceStatus | null> {
    if (!this.URL) return null;

    // AbortController rather than AbortSignal.timeout: the Outlook add-in runs in WebView2,
    // which may predate it.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), MAINTENANCE_STATUS_TIMEOUT_MS);

    try {
      const response = await fetch(this.URL, {
        method: "GET",
        headers: { Accept: "application/json" },
        cache: "no-store",
        signal: controller.signal,
      });

      const raw = await response.json().catch(() => null);
      return parseMaintenanceStatus(raw);
    } catch {
      return null;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Detects the end of an outage that a `503` raised, not the tools flag. Resolves `true` once
   * a request no longer returns `503`; a network error (no HTTP response) is NOT recovery, so a
   * user going offline mid-maintenance stays on the maintenance page.
   *
   * Bare unauthenticated `fetch` on purpose: probing through `apiClient` would run the auth
   * pipeline every poll, and an expired token would sign the user out while they sit on the
   * maintenance page. A `401` here is the healthy answer once the backend is back.
   */
  async isBackendReachable(): Promise<boolean> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), MAINTENANCE_STATUS_TIMEOUT_MS);

    try {
      const response = await fetch(`${API_URL}/accounts/users/me/`, {
        method: "GET",
        cache: "no-store",
        signal: controller.signal,
      });
      return response.status !== 503;
    } catch {
      return false;
    } finally {
      clearTimeout(timeoutId);
    }
  }
}

const maintenanceService = new MaintenanceService();
export default maintenanceService;
